/* Copyright (c) 2020-present CloudIO Inc */

import { atom, useStateXAtom, useStateXAtomSetter } from '@cloudio-saas/statex';
import { getOAuthUrl, getUrl } from 'config/config';
import { useOrgNameAndLogo } from 'layout/Header/OrgLogo';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo, useEffect, useLayoutEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useUser, {
  getHomePage,
  userAtom,
  UserSession,
  userValidatedAtom,
} from 'root/useUser';
import { ulid } from 'ulid';
import { getURLQueryStringToPost } from 'utils/url';
import WaveDots from 'utils/WaveDots';
import useNavigator from './useNavigator';
import { handleError } from 'platform/ErrorBoundary';
import { getErrorMessage } from 'utils/isEmpty';

export const xTokenAtom = atom({
  path: ['xToken'],
  defaultValue: { x: '', token: '' },
});

export async function preValidate(token: string): Promise<string> {
  const response = await fetch(`${getUrl()}x`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-Application': 'SignIn',
      'Content-Type': 'application/json',
      'X-Token': token,
    },
    body: JSON.stringify({ x: token }),
  });
  if (response.ok) {
    const { x } = await response.json();
    return x;
  } else {
    const text = await response.text();
    throw new Error(
      `Unable to perform pre validation. Server Response: ${text}`,
    );
  }
}

function HomePageWatcher({
  isAuthenticated,
}: {
  isAuthenticated?: boolean;
}): React.ReactElement | null {
  const platform = usePlatform();
  const navigator = useNavigator();
  const location = useLocation();
  const pathname = location?.pathname || '/';
  const user = useUser();

  useLayoutEffect(() => {
    if (pathname === '/') {
      const defaultHome =
        user.profiles['cloudio.DEFAULT_HOME_ON_SIGNIN'] ?? 'N';
      if (defaultHome !== 'Y') {
        const homePage = getHomePage(platform);
        if (homePage !== '/') {
          navigator.push(homePage);
        }
      }
    }
  }, [navigator, pathname, platform, user.profiles]);

  return isAuthenticated ? null : (
    <Navigate to="/signin" state={{ from: location }} />
  );
}

export async function isValidSession(
  session: UserSession,
): Promise<'ERROR' | 'OK' | 'DOWN'> {
  const token = `X${ulid()}`;
  try {
    const x = await preValidate(token);

    let query = getURLQueryStringToPost(session);
    if (query) query = `?${query}`;
    const response = await fetch(`${getUrl()}v${query}`, {
      method: 'POST',
      headers: {
        'X-Application': 'SignIn',
        'Content-Type': 'application/json',
        'X-Token': `${token}${x}`,
      },
      credentials: 'include',
      body: JSON.stringify({ token, x }),
    });

    const r: UserSession | { status: 'ERROR'; message: string } =
      await response.json();
    return r.status;
  } catch (e) {
    console.error(e);
    return 'DOWN';
  }
}

function SecureRoute({
  children,
}: {
  children: JSX.Element;
}): React.ReactElement | null {
  const platform = usePlatform();
  const [validated, setValidated] = useStateXAtom(userValidatedAtom);
  const user = useUser();
  const setXTokenAtom = useStateXAtomSetter(xTokenAtom);
  const [{ authProvider, pending, isNativeEnabled }] = useOrgNameAndLogo();

  useEffect(() => {
    // wait till the authProvider is loaded
    if (validated || pending) return;
    const search = new URLSearchParams(window.location.search);
    const impersonationToken = search.get('impersonation_token');
    let isImpersonating = false;
    let effectiveToken: string | null = null;
    if (impersonationToken) {
      sessionStorage.setItem('cloudio_is_impersonating', 'true');
      sessionStorage.setItem('cloudio_impersonation_token', impersonationToken);
      search.delete('impersonation_token');
      const newUrl = `${window.location.pathname}${
        search.toString() ? `?${search}` : ''
      }${window.location.hash || ''}`;
      window.history.replaceState({}, document.title, newUrl);
      isImpersonating = true;
      effectiveToken = impersonationToken;
    } else {
      const storedFlag = sessionStorage.getItem('cloudio_is_impersonating');
      const storedToken = sessionStorage.getItem('cloudio_impersonation_token');
      isImpersonating = storedFlag === 'true' && !!storedToken;
      effectiveToken = storedToken;
    }
    const token = `X${ulid()}`;
    preValidate(token)
      .then(async (x) => {
        setXTokenAtom({ x, token });
        const query = (() => {
          const q = getURLQueryStringToPost();
          return q ? `?${q}` : '';
        })();
        const impersonationFlag = isImpersonating ? '?&x_imp=1' : '';
        const url = isImpersonating
          ? `${getUrl()}impersonate_login${impersonationFlag}`
          : `${getUrl()}v${query}`;

        const body = isImpersonating ? { token: effectiveToken } : { token, x };
        try {
          const resp = await fetch(url, {
            method: 'POST',
            headers: {
              'X-Application': 'SignIn',
              'Content-Type': 'application/json',
              'X-Token': `${token}${x}`,
            },
            credentials: 'include',
            body: JSON.stringify(body),
          });

          if (!resp.ok) {
            const text = await resp.text();
            const prefix = isImpersonating
              ? 'Unable to complete impersonation.'
              : 'Unable to validate the session.';
            platform.showError(`${prefix} Server Response: ${text}`);

            if (isImpersonating) {
              sessionStorage.removeItem('cloudio_is_impersonating');
              sessionStorage.removeItem('cloudio_impersonation_token');
            }
            return;
          }

          const r = await resp.json();
          if (r.status === 'OK') {
            platform.forceUpdater(() => {
              platform.setter(userAtom, {
                ...r,
                isAuthenticated: true,
                roles: [],
              });
            });
          } else if (isImpersonating) {
            sessionStorage.removeItem('cloudio_is_impersonating');
            sessionStorage.removeItem('cloudio_impersonation_token');
          }
        } catch (err) {
          if (isImpersonating) {
            sessionStorage.removeItem('cloudio_is_impersonating');
            sessionStorage.removeItem('cloudio_impersonation_token');
          }
          platform.showError(getErrorMessage(err));
        }
      })
      .catch((e) => platform.showError(getErrorMessage(e)))
      .finally(() => setValidated(true));
  }, [pending, platform, setValidated, setXTokenAtom, validated]);

  const is_admin_url = window.location.href.indexOf('/admin') > -1;
  const { isAuthenticated } = user;
  useLayoutEffect(() => {
    if (validated) {
      if (
        !(is_admin_url || isAuthenticated) &&
        authProvider !== 'NATIVE' &&
        !isNativeEnabled
      ) {
        window.location.href = `${getOAuthUrl()}`;
      }
    }
  }, [
    authProvider,
    is_admin_url,
    platform,
    isAuthenticated,
    validated,
    isNativeEnabled,
  ]);

  useLayoutEffect(() => {
    if (validated && isAuthenticated) {
      async function errorHandler(event: ErrorEvent) {
        handleError(
          user,
          event.error,
          event.filename,
          event.lineno,
          event.colno,
        );
      }
      window.addEventListener('error', errorHandler);
      return () => {
        window.removeEventListener('error', errorHandler);
      };
    }
  }, [isAuthenticated, user, validated]);

  if (!validated) {
    return <WaveDots active reason="Validating Session..." />;
  }
  if (
    !(is_admin_url || isAuthenticated) &&
    authProvider !== 'NATIVE' &&
    !isNativeEnabled
  ) {
    // if the user is not authenticated and the auth provider is not native
    // redirect to the oauth url happens in the useLayoutEffect above
    // hence we return null here
    return null;
  }

  return isAuthenticated ? (
    <>
      {children}
      <HomePageWatcher isAuthenticated />
    </>
  ) : (
    <HomePageWatcher />
  );
}

export default memo(SecureRoute);
